// src/app/pages/dashboard/Apps/InvoiceProcessingWrapper.tsx

import React, { useState, useRef, useEffect, FC, useCallback } from 'react';
import axios from 'axios';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { API_URL } from '../../../modules/auth/core/_requests';
import { PageTitle } from '../../../../_designereyes/layout/core';    // PrimeReact Theme
import 'primereact/resources/primereact.min.css';               // PrimeReact Core CSS
import 'primeicons/primeicons.css';                             // PrimeReact Icons
import { Toast } from 'primereact/toast';                       // Import PrimeReact's Toast
import './ProductImagesUploaderWrapper.css';
import { useDropzone } from 'react-dropzone';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog } from 'primereact/confirmdialog';

interface ImageMetadata {
  originalName: string;
  imageCategory: string;
  imageType: string;
  upc: string;
  model: string;
  colorCode: string;
  brand: string;
  vendor_name: string;
  sku_1: string;
  category: string;
  size_code: string;
  size: string;
  color: string;
}



// Update ImagePreviewData interface
interface ImagePreviewData {
  file: File;
  previewUrl: string;
  originalName: string;
  imageCategory: string;
  imageType: string;
  upc: string;
  model: string;
  colorCode: string;
  brand: string;
  vendor_name: string;
  sku_1: string;
  category: string;
  size_code: string;
  size: string;
  color: string;
  isMetadataLoaded?: boolean;
}

interface QueryModeOption {
  label: string;
  value: string;
  tooltip: string;
}


interface ImageTypeOption {
  label: string;
  value: string;
  tooltip: string;
}

const imageTypeOptions: ImageTypeOption[] = [
  {
    label: 'Infer from Filename',
    value: 'Infer',
    tooltip: 'Image type will be inferred from the filename'
  },
  {
    label: 'Front View',
    value: 'Front',
    tooltip: 'All images will be set as front view product images'
  },
  {
    label: 'Angled View',
    value: 'Angled',
    tooltip: 'All images will be set as angled view product images'
  },
  {
    label: 'Side View',
    value: 'Side',
    tooltip: 'All images will be set as side view product images'
  },
  {
    label: 'Extra View',
    value: 'Extra',
    tooltip: 'All images will be set as extra view product images'
  }
];


const queryModeOptions: QueryModeOption[] = [
  {
    label: 'Infer from Filename',
    value: 'infer',
    tooltip: 'Image category will be inferred from the filename'
  },
  {
    label: 'Query as UPC Product Images',
    value: 'product',
    tooltip: 'All images will be queried as product images based on their UPC'
  },
  {
    label: 'Query as Model Lifestyle Images',
    value: 'model',
    tooltip: 'All images will be queried as model lifestyle images based on their model name'
  }
];


const ProductImagesUploaderPage: FC = () => {
  const toast = useRef<Toast>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState<ImagePreviewData[]>([]);
  const [uploadMode, setUploadMode] = useState('valid_only');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<ImagePreviewData[]>([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [queryMode, setQueryMode] = useState('infer');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [imageTypeMode, setImageTypeMode] = useState('Infer');

  const fetchMetadata = async (fileNames: string[]) => {
    try {
      const response = await axios.post(`${API_URL}/api/product-images/upload/query-metadata`, {
        queryMode: queryMode,
        imageTypeMode: imageTypeMode,
        fileNames: fileNames
      });

  
      const { summary } = response.data;
      
      // Show separate warning message if any failed
      if (summary.failed > 0) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Metadata Lookup',
          detail: `Metadata not found for ${summary.failed} images`,
          life: 5000
        });
      }
  
      // Always show success message if any were successful
      if (summary.successful > 0) {
        toast.current?.show({
          severity: 'success',
          summary: 'Metadata Lookup',
          detail: `Found metadata for ${summary.successful} images`,
          life: 3000
        });
      }
  
      return response.data.data;
    } catch (error) {
      console.error('Error fetching metadata:', error);
      
      // Check if the error is axios error and has status code 413
      if (axios.isAxiosError(error) && error.response?.status === 413) {
        toast.current?.show({
          severity: 'error',
          summary: 'Request Too Large',
          detail: 'Too many images selected. Please try uploading fewer images at a time.',
          life: 5000
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch image metadata'
        });
      }
      return [];
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    try {
      // Immediately show the images with loading state
      const newImages: ImagePreviewData[] = acceptedFiles
        .filter(file => file.type.startsWith('image/'))
        .map(file => ({
          file,
          previewUrl: URL.createObjectURL(file),
          originalName: file.name,
          imageCategory: '',
          imageType: '',
          upc: '',
          model: '',
          colorCode: '',
          brand: '',
          vendor_name: '',
          sku_1: '',
          category: '',
          size_code: '',
          size: '',
          color: '',
          isMetadataLoaded: false
        }));
  
      setImages(prev => [...prev, ...newImages]);
      
      setIsLoading(true);
      const fileNames = newImages.map(img => img.originalName);
      const metadata = await fetchMetadata(fileNames);
  
      // Update images with fetched metadata
      setImages(prev => prev.map(img => {
        const matchingMetadata = metadata.find((m: ImageMetadata) => 
          m.originalName === img.originalName
        );
        
        return {
          ...img,
          ...(matchingMetadata || {}), // Spread empty object if no metadata found
          isMetadataLoaded: true // Mark as loaded regardless of whether metadata was found
        };
      }));
  
    } catch (error) {
      console.error('Error processing files:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to process some files'
      });
    } finally {
      setIsLoading(false);
    }
  }, [queryMode, imageTypeMode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    }
  });

  const removeImage = (index: number) => {
    const imageToRemove = images[index];
    setImages(prev => prev.filter((_, i) => i !== index));
    // Also remove from selectedImages if present
    setSelectedImages(prev => prev.filter(img => img.originalName !== imageToRemove.originalName));
  };

  const initiateUpload = () => {
    const imagesToUpload = selectedImages.filter(img => img.imageCategory);
    
    if (selectedImages.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select at least one image to upload.'
      });
      return;
    }

    if (imagesToUpload.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No valid images to upload. Upload requires at least one valid image to be selected.'
      });
      return;
    }

    // Show confirmation dialog with details
    setShowConfirmDialog(true);
  };
  

  const handleUpload = async () => {
    // Filter out images without metadata - only imageCategory is mandatory
    const imagesToUpload = selectedImages.filter(img => img.imageCategory);
    const hasLoadingImages = images.some(img => !img.isMetadataLoaded);

    
    if (selectedImages.length > imagesToUpload.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: `Skipping ${selectedImages.length - imagesToUpload.length} images due to missing metadata`,
        life: 5000
      });
    }
    
    if (imagesToUpload.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No valid images to upload. Upload requires at least one valid image to be selected.'
      });
      return;
    }
  
    try {
      setIsUploading(true);
      const formData = new FormData();
      imagesToUpload.forEach((imageData) => {
        formData.append('images', imageData.file);
        formData.append('metadata', JSON.stringify({
          originalName: imageData.originalName,
          imageCategory: imageData.imageCategory,
          imageType: imageData.imageType,
          upc: imageData.upc,
          model: imageData.model,
          colorCode: imageData.colorCode,
          brand: imageData.brand,
          vendor_name: imageData.vendor_name,
          sku_1: imageData.sku_1,
          category: imageData.category,
          size_code: imageData.size_code,
          size: imageData.size,
          color: imageData.color
        }));
      });
      formData.append('uploadMode', uploadMode);  
  
      const response = await axios.post(`${API_URL}/api/product-images/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
  
      if (response.data.success) {
        // Only remove the successfully uploaded images
        setImages(prevImages => 
          prevImages.filter(img => !imagesToUpload.some(uploaded => uploaded.originalName === img.originalName))
        );
        // Clear only the successfully uploaded images from selection
        setSelectedImages(prev => 
          prev.filter(img => !imagesToUpload.some(uploaded => uploaded.originalName === img.originalName))
        );
  
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Successfully uploaded ${response.data.results.successful} images`
        });
      } else {
        // Handle partial or complete failure
        const failedCount = response.data.results.failed;
        const successCount = response.data.results.successful;
        
        if (successCount > 0) {
          // Some files succeeded, some failed
          // Remove successful uploads from the display list
          setImages(prevImages => {
            const failedFilenames = response.data.results.failures?.map((f: any) => f.filename) || [];
            return prevImages.filter(img => 
              failedFilenames.includes(img.originalName) || 
              !imagesToUpload.some(uploaded => uploaded.originalName === img.originalName)
            );
          });
  
          toast.current?.show({
            severity: 'warn',
            summary: 'Partial Success',
            detail: `Uploaded ${successCount} images, ${failedCount} failed`
          });
        } else {
          // All files failed
          toast.current?.show({
            severity: 'error',
            summary: 'Upload Failed',
            detail: 'Failed to upload all images'
          });
        }
  
        // Show individual failure messages
        if (response.data.results.failures) {
          const failureCount = response.data.results.failures.length;
          toast.current?.show({
            severity: 'error',
            summary: 'Failed Uploads',
            detail: `${failureCount} ${failureCount === 1 ? 'image' : 'images'} failed to upload.`,
            life: 5000
          });
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error instanceof Error ? error.message : 'Failed to upload images'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setImages([]);
    setSelectedImages([]);
    toast.current?.show({
      severity: 'info',
      summary: 'Info',
      detail: 'Image list cleared'
    });
  };

  const renderPreviewTable = () => (
    
    <DataTable 
      value={images}
      selection={selectedImages}
      onSelectionChange={(e) => setSelectedImages(e.value)}
      dataKey="originalName"
      paginator
      rows={rows}
      first={first}
      onPage={(e) => setFirst(e.first)}
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
      selectionMode="multiple"
      className="table-dark"
    >
      <Column selectionMode="multiple" headerStyle={{width: '3rem'}}></Column>
      <Column header="Preview" body={(rowData) => (
        <img
          src={rowData.previewUrl}
          alt={`Preview`}
          style={{ width: '50px', height: '50px', objectFit: 'cover' }}
        />
      )} />
      <Column field="originalName" header="Original Name" />
      <Column header="Image Category" body={(rowData) => (
        !rowData.isMetadataLoaded ? (
          <div className="d-flex align-items-center">
            <i className="pi pi-spin pi-spinner me-2"></i>
            Loading...
          </div>
        ) : (
          rowData.imageCategory || (
            <span className="text-muted">
              <i className="pi pi-exclamation-circle me-2"></i>
              Not Found
            </span>
          )
        )
      )} />
      <Column field="imageType" header="Image Type" />
      <Column field="upc" header="UPC" />
      <Column field="model" header="Model" />
      <Column field="colorCode" header="Color Code" />
      <Column field="brand" header="Brand" />
      <Column field="size" header="Size" />
      <Column field="color" header="Color" />
      <Column header="Actions" body={(rowData, options) => (
        <Button
          icon="pi pi-trash"
          onClick={() => removeImage(options.rowIndex)}
          severity="danger"
          size="small"
          aria-label="Remove image"
        />
      )} />
    </DataTable>
  );
  
  

  return (
    <div className='card text-light position-relative'>
      <Toast ref={toast} position="bottom-left" />

      <ConfirmDialog 
        visible={showConfirmDialog} 
        onHide={() => setShowConfirmDialog(false)}
        message={`Are you sure you want to upload ${selectedImages.length} images? ${
          uploadMode === 'force_upload' 
            ? 'This will overwrite any existing images.' 
            : ''
        }`}
        header="Confirm Upload"
        icon="pi pi-exclamation-triangle"
        accept={handleUpload}
        reject={() => setShowConfirmDialog(false)}
        acceptLabel="Yes, Upload"
        rejectLabel="Cancel"
      />

      <ConfirmDialog 
        visible={showConfirmDialog} 
        onHide={() => setShowConfirmDialog(false)}
        message={`Are you sure you want to upload ${selectedImages.length} images? ${
          uploadMode === 'force_upload' 
            ? 'This will overwrite any existing images.' 
            : ''
        }`}
        header="Confirm Upload"
        icon="pi pi-exclamation-triangle"
        accept={handleUpload}
        reject={() => setShowConfirmDialog(false)}
        acceptLabel="Yes, Upload"
        rejectLabel="Cancel"
      />

      <div className='card-header d-flex justify-content-between align-items-center mb-1'>
        <h2 className='card-title mb-0 mt-0'>Upload Product Images</h2>
      </div>

      <div className='card-body pt-4 text-body bg-body'>

        {/* Query Mode Selection */}
        <div className="query-mode-selection mb-4">
          <h4 className="mb-2">Image Category Query Mode</h4>
          <div className="d-flex align-items-center gap-2">
            <Dropdown
              value={queryMode}
              onChange={(e) => setQueryMode(e.value)}
              options={queryModeOptions}
              optionLabel="label"
              className="w-100"
              style={{ maxWidth: '300px' }}
            />
            <i 
              className="pi pi-question-circle" 
              style={{ cursor: 'pointer', color: 'white' }}
              title={queryModeOptions.find(opt => opt.value === queryMode)?.tooltip}
            />
          </div>
        </div>
        {/* New Image Type Selection */}
        <div className="query-mode-selection mb-4">
          <h4 className="mb-2">Image Type Mode</h4>
          <div className="d-flex align-items-center gap-2">
            <Dropdown
              value={imageTypeMode}
              onChange={(e) => setImageTypeMode(e.value)}
              options={imageTypeOptions}
              optionLabel="label"
              className="w-100"
              style={{ maxWidth: '300px' }}
            />
            <i 
              className="pi pi-question-circle" 
              style={{ cursor: 'pointer', color: 'white' }}
              title={imageTypeOptions.find(opt => opt.value === imageTypeMode)?.tooltip}
            />
          </div>
        </div>

        {/* Upload Mode Selection */}
        <div className="upload-mode-selection mb-4 mt-4">
        <h4 className="mb-2">Upload Mode</h4>
          <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-center">
              <RadioButton
                inputId="mode2"
                name="uploadMode"
                value="valid_only"
                onChange={(e) => setUploadMode(e.value)}
                checked={uploadMode === 'valid_only'}
              />
              <label htmlFor="mode2" className="ms-2 cursor-pointer">Upload All Valid Images</label>
              <i 
                className="pi pi-question-circle ms-2" 
                style={{ cursor: 'pointer'}}
                title="Skips files that already exist on the server"
              />
            </div>
            
            <div className="d-flex align-items-center">
              <RadioButton
                inputId="mode3"
                name="uploadMode"
                value="force_upload"
                onChange={(e) => setUploadMode(e.value)}
                checked={uploadMode === 'force_upload'}
              />
              <label htmlFor="mode3" className="ms-2 cursor-pointer">Force Replace Images</label>
              <i 
                className="pi pi-question-circle ms-2" 
                style={{ cursor: 'pointer' }}
                title="Overwrites any existing files"
              />
            </div>
          </div>
        </div>

        {/* Upload Controls */}
        <div className="d-flex gap-3 mb-6 mt-3">
        <Button 
          label={isUploading 
            ? `Uploading ${selectedImages.length} images...` 
            : `Upload ${selectedImages.length} Images`}
          icon={isUploading ? "pi pi-spinner pi-spin" : "pi pi-upload"}
          onClick={initiateUpload}
          severity="success"
          disabled={isUploading}
        />
          <Button 
            label="Clear All" 
            icon="pi pi-trash" 
            onClick={handleClear}
            severity="danger"
            disabled={isLoading}
          />
        </div>

        {/* Drag & Drop Zone */}
        <div 
          {...getRootProps()} 
          className={`dropzone-area my-4 p-5 text-center ${isDragActive ? 'active' : ''}`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p className="mb-0">Drop the files or folders here ...</p>
          ) : (
            <p className="mb-0">Drag 'n' drop image files or folders here, or click to select files</p>
          )}
        </div>

        {/* Preview Table */}
        {images.length > 0 && (
          <div className='image-preview-area my-4'>
          <div className="d-flex align-items-center gap-2">
            <h3>Image Preview List ({images.length})</h3>
            <i 
              className="pi pi-question-circle"
              style={{ cursor: 'pointer' }}
              title="Select images from the list below and click the Upload button to process them"
            />
          </div>
          {renderPreviewTable()}
        </div>
        )}
      </div>
    </div>
  );
};


export const ProductImagesUploaderWrapper: FC = () => {
  return (
    <>
      <PageTitle>Product Images Uploader</PageTitle>
      <ProductImagesUploaderPage />
    </>
  );
};

export default ProductImagesUploaderWrapper;